import { DomainError } from '../errors.js';
import type { LocalDate } from '../local-date.js';
import {
  assertLimits,
  evaluateMeasurement,
  LIMIT_SOURCES,
  type LimitSource,
  type LimitsSnapshot,
  limitsSnapshotOf,
  type MeasurementLimits,
  type MeasurementResult,
} from './measurements.js';

/**
 * CM (condition monitoring) içe aktarma (plan M7 Task 16; ADR 0029 K19, K20): gemideki CM
 * sisteminden ya da onaylı servis sağlayıcısından gelen baseline, eşik ve ölçümler
 * [NR496 4.4.2d]. Saf fonksiyon: dosyayı okumak (CSV, XLSX) sunucunun işidir (Task 19). Önizleme ve
 * uygulama aynı planı kullanır; hatalı satırı olan dosya uygulanmaz (kısmi içe aktarma yok).
 */

/**
 * Tek şablon (K20). Son iki sütun isteğe bağlıdır: yalnız eşik içeren dosya geçerlidir. Sınırın
 * dahil ya da hariç işareti şablonda yoktur: yeni noktada dahil (K18 varsayılanı), var olan
 * noktada noktanınki korunur.
 */
export const CM_TEMPLATE_COLUMNS = [
  'Equipment Code',
  'Point',
  'Unit',
  'Baseline',
  'Baseline Change Reason',
  'Alert Low',
  'Alert High',
  'Danger Low',
  'Danger High',
  'Limit Source',
  'Provider',
  'Measured At',
  'Value',
] as const;
export type CmColumn = (typeof CM_TEMPLATE_COLUMNS)[number];

const OPTIONAL_COLUMNS: readonly CmColumn[] = ['Measured At', 'Value'];

/** Bir dosyada en çok bu kadar veri satırı. */
export const CM_MAX_ROWS = 20_000;

/** Hücre: CSV'de metin, XLSX'te metin ya da sayı (tarih okuyucuda metne çevrilir). */
export type CmCell = string | number | null;

export interface CmFile {
  /** İlk satır: sütun adları. */
  header: readonly CmCell[];
  /** Veri satırları; numarası başlık 1 olmak üzere dizindeki yerinin iki fazlası. */
  rows: readonly (readonly CmCell[])[];
}

/** Var olan (silinmemiş) ölçüm noktası. */
export interface CmExistingPoint extends MeasurementLimits {
  id: string;
  equipmentId: string;
  name: string;
  unit: string;
  limitSource: LimitSource;
  provider: string | null;
  baselineValue: number | null;
  baselineOriginal: number | null;
  autoDefect: boolean;
  changeReason: string | null;
}

export interface CmImportContext {
  fileName: string;
  /** Dosyanın SHA-256'sı (64 küçük hex). */
  fileSha256: string;
  /** Bu gemide daha önce içe aktarılmış dosyaların SHA-256'ları; iptal edilenler dahil. */
  previousImports: readonly string[];
  /** Geminin silinmemiş ekipmanı. */
  equipment: readonly { id: string; code: string }[];
  /** Geminin silinmemiş ölçüm noktaları. */
  points: readonly CmExistingPoint[];
  /** Açık otomatik arızası olan noktalar (K19). */
  openAutoDefectPointIds: ReadonlySet<string>;
  /** Var olan iptal edilmemiş ölçümler: `pointId|measuredAt` (ISO-8601 UTC). */
  existingMeasurements: ReadonlySet<string>;
  /** Gemi sunucusunun saati, ISO-8601 UTC: gelecek tarih buna göre [NR496 4.5.2a]. */
  serverNow: string;
  /** Geminin saat dilimi: verilen UTC anındaki fark (dakika). */
  offsetMinutes: (utcMs: number) => number;
  /** Ölçüm değeri yazılabilir mi: gemide evet, ofiste (kurulum devri) hayır (K20). */
  valuesAllowed: boolean;
}

export interface CmRowError {
  /** Dosyadaki satır numarası (başlık 1). */
  row: number;
  column?: CmColumn;
  code: string;
  value?: string;
}

/** Ölçüm noktasının eşik ve baseline alanları. */
export interface CmPointValues extends MeasurementLimits {
  unit: string;
  limitSource: LimitSource;
  provider: string | null;
  baselineValue: number | null;
  baselineOriginal: number | null;
  baselineTakenOn: LocalDate | null;
  changeReason: string | null;
}

export interface CmPointToCreate extends CmPointValues {
  row: number;
  equipmentId: string;
  equipmentCode: string;
  name: string;
  autoDefect: boolean;
}

export interface CmPointToUpdate {
  row: number;
  pointId: string;
  /** Değişen alanlar [UR Z27 2.2.4]. */
  changes: string[];
  before: CmPointValues;
  after: CmPointValues;
}

/** Ölçümün noktası: var olan ya da bu dosyayla açılacak (`pointsToCreate` sırası). */
export type CmPointRef = { pointId: string } | { newPointIndex: number };

export interface CmMeasurementToAdd {
  row: number;
  point: CmPointRef;
  measuredAt: string;
  localDate: LocalDate;
  tzOffsetMin: number;
  value: number;
  unit: string;
  limitsSnapshot: LimitsSnapshot;
  result: MeasurementResult;
}

export interface CmDefectToOpen {
  point: CmPointRef;
  /** Bağlandığı ölçüm: `measurementsToAdd` içindeki yeri (noktanın en son FAIL ölçümü). */
  measurementIndex: number;
  row: number;
}

export interface CmImportPlan {
  mode: 'thresholds' | 'thresholds_and_values';
  rowsTotal: number;
  errors: CmRowError[];
  /** Uygulamayı engellemeyen notlar: zaten kayıtlı ölçüm (`MEASUREMENT_EXISTS`) yazılmaz. */
  warnings: { row: number; code: string }[];
  pointsToCreate: CmPointToCreate[];
  pointsToUpdate: CmPointToUpdate[];
  measurementsToAdd: CmMeasurementToAdd[];
  defectsToOpen: CmDefectToOpen[];
}

const fold = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();
const textOf = (cell: CmCell | undefined): string =>
  cell === null || cell === undefined ? '' : String(cell).normalize('NFC').trim();

/** Ondalık ayraç nokta ya da virgül; binlik ayraç kabul edilmez (belirsiz). */
function numberOf(cell: CmCell | undefined): number | null | 'invalid' {
  if (typeof cell === 'number') return Number.isFinite(cell) ? cell : 'invalid';
  const text = textOf(cell);
  if (text === '') return null;
  if (!/^[-+]?(\d+([.,]\d+)?|[.,]\d+)$/.test(text)) return 'invalid';
  return Number(text.replace(',', '.'));
}

function limitSourceOf(text: string): LimitSource | null {
  const key = text.toLowerCase().replace(/[\s-]+/g, '_');
  return (LIMIT_SOURCES as readonly string[]).includes(key) ? (key as LimitSource) : null;
}

const MINUTE_MS = 60_000;
const ZONED = /(Z|[+-]\d{2}:?\d{2})$/i;
const LOCAL = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/**
 * Ölçüm anı: bölgeli ISO-8601 (UTC'ye çevrilir) ya da gemi yerel saati (`YYYY-MM-DD[ HH:mm[:ss]]`;
 * saat yoksa gece yarısı). Geçersizse `undefined`.
 */
function instantOf(text: string, offsetMinutes: (utcMs: number) => number): string | undefined {
  if (ZONED.test(text)) {
    const at = Date.parse(text);
    return Number.isNaN(at) ? undefined : new Date(at).toISOString();
  }
  const match = LOCAL.exec(text);
  if (!match) return undefined;
  const [, y, mo, d, h = '0', mi = '0', se = '0'] = match;
  const wall = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(se));
  const check = new Date(wall);
  if (
    check.getUTCFullYear() !== Number(y) ||
    check.getUTCMonth() !== Number(mo) - 1 ||
    check.getUTCDate() !== Number(d) ||
    Number(h) > 23 ||
    Number(mi) > 59 ||
    Number(se) > 59
  ) {
    return undefined;
  }
  // Duvar saatinden UTC'ye: fark UTC anına göre; yaz saati geçişinde ikinci tahmin
  const first = wall - offsetMinutes(wall) * MINUTE_MS;
  const utc = wall - offsetMinutes(first) * MINUTE_MS;
  return new Date(utc).toISOString();
}

const localDateAt = (at: string, offsetMin: number): LocalDate =>
  new Date(Date.parse(at) + offsetMin * MINUTE_MS).toISOString().slice(0, 10);

/** Satırın tanımladığı nokta değerleri (eşik ve baseline). */
interface RowDefinition {
  row: number;
  equipmentId: string;
  equipmentCode: string;
  name: string;
  unit: string;
  limits: MeasurementLimits;
  limitSource: LimitSource;
  provider: string | null;
  baseline: number | null;
  baselineReason: string | null;
  measurement?: { at: string; raw: string; value: number };
}

const LIMIT_COLUMNS: readonly [CmColumn, keyof MeasurementLimits][] = [
  ['Alert Low', 'alertLow'],
  ['Alert High', 'alertHigh'],
  ['Danger Low', 'dangerLow'],
  ['Danger High', 'dangerHigh'],
];

/** `assertLimits` ayrıntısından hatanın sütunu. */
function limitsColumn(error: DomainError): CmColumn {
  if (error.code === 'LIMITS_REQUIRED') return 'Alert Low';
  if (error.message.includes('danger low')) return 'Danger Low';
  if (error.message.includes('danger high')) return 'Danger High';
  return 'Alert High';
}

const VALUE_KEYS = [
  'unit',
  'limitSource',
  'provider',
  'alertLow',
  'alertHigh',
  'dangerLow',
  'dangerHigh',
  'lowInclusive',
  'highInclusive',
  'baselineValue',
] as const;

const COLUMN_OF: Readonly<Record<(typeof VALUE_KEYS)[number], CmColumn>> = {
  unit: 'Unit',
  limitSource: 'Limit Source',
  provider: 'Provider',
  alertLow: 'Alert Low',
  alertHigh: 'Alert High',
  dangerLow: 'Danger Low',
  dangerHigh: 'Danger High',
  lowInclusive: 'Alert Low',
  highInclusive: 'Alert High',
  baselineValue: 'Baseline',
};

/**
 * CM dosyasının planı (K19, K20) [NR496 4.4.2d]:
 * - Dosya düzeyi (`DomainError`): aynı dosya (SHA-256) ikinci kez uygulanmaz, iptalden sonra da
 *   (`ALREADY_IMPORTED`); şablon sütunu eksik (`MISSING_COLUMN`); veri satırı yok (`EMPTY_FILE`)
 *   ya da çok (`TOO_MANY_ROWS`).
 * - Satır hataları satır numarasıyla: bilinmeyen ekipman, birim uyuşmazlığı, limit sırası, limit
 *   kaynağı, gelecek tarih [NR496 4.5.2a], aynı nokta ve zaman iki kez, aynı noktanın satırlarında
 *   eşik çelişkisi, baseline değişikliğinde gerekçe, ofiste değer (`VALUES_ONLY_ON_BOARD`).
 * - Plan: açılacak noktalar, eşiği değişecek noktalar eski ve yeni değerleriyle [UR Z27 2.2.4],
 *   eklenecek ölçümler (limit anlık kopyası ve sonuçla), açılacak arızalar: FAIL çıkan her nokta
 *   için, o noktada açık otomatik arıza yoksa noktanın en son FAIL ölçümüne bağlı tek arıza (K19).
 * - İlk baseline bir kez yazılır, değişmez [UR Z27 2.3.2(iii)]; sonraki değişiklik gerekçe ister.
 */
export function planCmImport(file: CmFile, context: CmImportContext): CmImportPlan {
  if (context.previousImports.includes(context.fileSha256)) {
    throw new DomainError('ALREADY_IMPORTED', context.fileSha256);
  }
  const header = file.header.map((cell) => fold(textOf(cell)));
  const indexOf = new Map<CmColumn, number>();
  for (const column of CM_TEMPLATE_COLUMNS) {
    const index = header.indexOf(fold(column));
    if (index >= 0) indexOf.set(column, index);
    else if (!OPTIONAL_COLUMNS.includes(column)) throw new DomainError('MISSING_COLUMN', column);
  }
  const rows = file.rows.filter((cells) => cells.some((cell) => textOf(cell) !== ''));
  if (rows.length === 0) throw new DomainError('EMPTY_FILE');
  if (rows.length > CM_MAX_ROWS) throw new DomainError('TOO_MANY_ROWS', String(rows.length));

  const now = Date.parse(context.serverNow);
  const today = localDateAt(context.serverNow, context.offsetMinutes(now));
  const equipmentByCode = new Map(context.equipment.map((e) => [fold(e.code), e]));
  const existingByKey = new Map(context.points.map((p) => [`${p.equipmentId}|${fold(p.name)}`, p]));
  const errors: CmRowError[] = [];
  const definitions: RowDefinition[] = [];

  /** Satırın tanımı; hata varsa hata listeye yazılır ve `null`. */
  const parseRow = (cells: readonly CmCell[], rowNo: number): RowDefinition | null => {
    const cell = (column: CmColumn) => {
      const at = indexOf.get(column);
      return at === undefined ? null : (cells[at] ?? null);
    };
    const fail = (column: CmColumn | undefined, code: string, value?: string): null => {
      errors.push({
        row: rowNo,
        ...(column ? { column } : {}),
        code,
        ...(value !== undefined ? { value } : {}),
      });
      return null;
    };
    const code = textOf(cell('Equipment Code'));
    const equipment = equipmentByCode.get(fold(code));
    if (!equipment) return fail('Equipment Code', 'UNKNOWN_EQUIPMENT', code);
    const name = textOf(cell('Point')).replace(/\s+/g, ' ');
    if (name === '' || name.length > 200) return fail('Point', 'POINT_REQUIRED');
    const unit = textOf(cell('Unit')).replace(/\s+/g, ' ');
    if (unit === '' || unit.length > 20) return fail('Unit', 'UNIT_REQUIRED');
    const existing = existingByKey.get(`${equipment.id}|${fold(name)}`);
    if (existing && existing.unit !== unit) return fail('Unit', 'UNIT_MISMATCH', unit);

    const limits: MeasurementLimits = {
      alertLow: null,
      alertHigh: null,
      dangerLow: null,
      dangerHigh: null,
      // Şablonda dahil/hariç işareti yok: var olan noktanınki, yeni noktada dahil (K18)
      lowInclusive: existing?.lowInclusive ?? true,
      highInclusive: existing?.highInclusive ?? true,
    };
    for (const [column, key] of LIMIT_COLUMNS) {
      const value = numberOf(cell(column));
      if (value === 'invalid') return fail(column, 'INVALID_NUMBER', textOf(cell(column)));
      (limits as unknown as Record<string, number | null>)[key] = value;
    }
    try {
      assertLimits(limits);
    } catch (error) {
      if (!(error instanceof DomainError)) throw error;
      return fail(limitsColumn(error), error.code);
    }
    const sourceText = textOf(cell('Limit Source'));
    if (sourceText === '') return fail('Limit Source', 'LIMIT_SOURCE_REQUIRED');
    const limitSource = limitSourceOf(sourceText);
    if (!limitSource) return fail('Limit Source', 'INVALID_LIMIT_SOURCE', sourceText);
    const providerText = textOf(cell('Provider'));
    const provider = providerText === '' ? null : providerText.slice(0, 200);
    if (limitSource === 'cm_provider' && provider === null) {
      return fail('Provider', 'PROVIDER_REQUIRED');
    }
    const baseline = numberOf(cell('Baseline'));
    if (baseline === 'invalid') return fail('Baseline', 'INVALID_NUMBER', textOf(cell('Baseline')));
    const reasonText = textOf(cell('Baseline Change Reason'));
    const baselineReason = reasonText === '' ? null : reasonText.slice(0, 500);
    if (
      existing?.baselineValue !== null &&
      existing?.baselineValue !== undefined &&
      baseline !== null &&
      baseline !== existing.baselineValue &&
      baselineReason === null
    ) {
      return fail('Baseline Change Reason', 'BASELINE_CHANGE_REASON_REQUIRED');
    }

    const definition: RowDefinition = {
      row: rowNo,
      equipmentId: equipment.id,
      equipmentCode: equipment.code,
      name: existing?.name ?? name,
      unit,
      limits,
      limitSource,
      provider,
      baseline,
      baselineReason,
    };
    const atText = textOf(cell('Measured At'));
    const valueCell = cell('Value');
    const valueText = textOf(valueCell);
    if (atText !== '' || valueText !== '') {
      if (!context.valuesAllowed) return fail('Value', 'VALUES_ONLY_ON_BOARD');
      if (atText === '') return fail('Measured At', 'MEASURED_AT_REQUIRED');
      if (valueText === '') return fail('Value', 'VALUE_REQUIRED');
      const at = instantOf(atText, context.offsetMinutes);
      if (at === undefined) return fail('Measured At', 'INVALID_DATE', atText);
      // [NR496 4.5.2a] gelecek tarihli parametre kaydı yok; saat gemi sunucusunun
      if (Date.parse(at) > now) return fail('Measured At', 'FUTURE_DATE', atText);
      const value = numberOf(valueCell);
      if (value === 'invalid' || value === null) return fail('Value', 'INVALID_NUMBER', valueText);
      definition.measurement = { at, raw: atText, value };
    }
    return definition;
  };
  for (const [index, cells] of file.rows.entries()) {
    if (!cells.some((cell) => textOf(cell) !== '')) continue;
    const definition = parseRow(cells, index + 2);
    if (definition) definitions.push(definition);
  }

  // Aynı noktanın satırları aynı eşiği taşır; aynı nokta ve zaman iki kez yok
  const firstOf = new Map<string, RowDefinition>();
  const seenAt = new Set<string>();
  const valid: RowDefinition[] = [];
  for (const definition of definitions) {
    const key = `${definition.equipmentId}|${fold(definition.name)}`;
    const first = firstOf.get(key);
    if (first) {
      const a = valuesOf(first);
      const b = valuesOf(definition);
      const differs = VALUE_KEYS.find((k) => a[k] !== b[k]);
      if (differs) {
        errors.push({
          row: definition.row,
          column: COLUMN_OF[differs],
          code: 'CONFLICTING_THRESHOLDS',
        });
        continue;
      }
    } else {
      firstOf.set(key, definition);
    }
    if (definition.measurement) {
      const atKey = `${key}|${definition.measurement.at}`;
      if (seenAt.has(atKey)) {
        errors.push({
          row: definition.row,
          column: 'Measured At',
          code: 'DUPLICATE_MEASUREMENT',
          value: definition.measurement.raw,
        });
        continue;
      }
      seenAt.add(atKey);
    }
    valid.push(definition);
  }
  errors.sort((a, b) => a.row - b.row);

  const plan: CmImportPlan = {
    mode: definitions.some((d) => d.measurement) ? 'thresholds_and_values' : 'thresholds',
    rowsTotal: rows.length,
    errors,
    warnings: [],
    pointsToCreate: [],
    pointsToUpdate: [],
    measurementsToAdd: [],
    defectsToOpen: [],
  };
  if (errors.length > 0) return plan;

  const batchReason = `CM import ${context.fileName} (${context.fileSha256.slice(0, 12)})`;
  const refOf = new Map<string, CmPointRef>();
  const snapshotOf = new Map<string, LimitsSnapshot>();
  const autoDefectOf = new Map<string, boolean>();
  for (const [key, definition] of firstOf) {
    const existing = existingByKey.get(key);
    if (!valid.includes(definition)) continue;
    if (!existing) {
      const values: CmPointValues = {
        ...pointValues(definition),
        baselineOriginal: definition.baseline,
        baselineTakenOn: definition.baseline === null ? null : today,
        changeReason: batchReason,
      };
      refOf.set(key, { newPointIndex: plan.pointsToCreate.length });
      plan.pointsToCreate.push({
        row: definition.row,
        equipmentId: definition.equipmentId,
        equipmentCode: definition.equipmentCode,
        name: definition.name,
        autoDefect: true,
        ...values,
      });
      snapshotOf.set(key, limitsSnapshotOf(values));
      autoDefectOf.set(key, true);
      continue;
    }
    refOf.set(key, { pointId: existing.id });
    autoDefectOf.set(key, existing.autoDefect);
    const before: CmPointValues = {
      ...pointValues({
        unit: existing.unit,
        limits: existing,
        limitSource: existing.limitSource,
        provider: existing.provider,
        baseline: existing.baselineValue,
      }),
      baselineOriginal: existing.baselineOriginal,
      baselineTakenOn: null,
      changeReason: existing.changeReason,
    };
    // Baseline boş hücre "değişmedi" demektir: var olan baseline silinmez
    const baseline = definition.baseline ?? existing.baselineValue;
    const next = pointValues({ ...definition, baseline });
    const changes = VALUE_KEYS.filter((k) => before[k] !== next[k]);
    if (changes.length === 0) {
      snapshotOf.set(key, limitsSnapshotOf(before));
      continue;
    }
    const baselineChanged = changes.includes('baselineValue');
    const after: CmPointValues = {
      ...next,
      baselineOriginal: existing.baselineOriginal ?? baseline,
      baselineTakenOn: baselineChanged ? today : null,
      changeReason:
        baselineChanged && definition.baselineReason !== null
          ? definition.baselineReason
          : batchReason,
    };
    plan.pointsToUpdate.push({ row: definition.row, pointId: existing.id, changes, before, after });
    snapshotOf.set(key, limitsSnapshotOf(after));
  }

  const latestFail = new Map<string, number>();
  for (const definition of valid) {
    if (!definition.measurement) continue;
    const key = `${definition.equipmentId}|${fold(definition.name)}`;
    const point = refOf.get(key) as CmPointRef;
    const existing = existingByKey.get(key);
    const { at, value } = definition.measurement;
    if (existing && context.existingMeasurements.has(`${existing.id}|${at}`)) {
      plan.warnings.push({ row: definition.row, code: 'MEASUREMENT_EXISTS' });
      continue;
    }
    const snapshot = snapshotOf.get(key) as LimitsSnapshot;
    const offset = context.offsetMinutes(Date.parse(at));
    const result = evaluateMeasurement(value, snapshot);
    plan.measurementsToAdd.push({
      row: definition.row,
      point,
      measuredAt: at,
      localDate: localDateAt(at, offset),
      tzOffsetMin: offset,
      value,
      unit: definition.unit,
      limitsSnapshot: snapshot,
      result,
    });
    if (result !== 'fail') continue;
    const index = plan.measurementsToAdd.length - 1;
    const previous = latestFail.get(key);
    if (
      previous === undefined ||
      (plan.measurementsToAdd[previous] as CmMeasurementToAdd).measuredAt < at
    ) {
      latestFail.set(key, index);
    }
  }
  for (const [key, index] of latestFail) {
    const ref = refOf.get(key) as CmPointRef;
    if (autoDefectOf.get(key) !== true) continue;
    if ('pointId' in ref && context.openAutoDefectPointIds.has(ref.pointId)) continue;
    const measurement = plan.measurementsToAdd[index] as CmMeasurementToAdd;
    plan.defectsToOpen.push({ point: ref, measurementIndex: index, row: measurement.row });
  }
  plan.defectsToOpen.sort((a, b) => a.row - b.row);
  return plan;
}

function pointValues(definition: {
  unit: string;
  limits: MeasurementLimits;
  limitSource: LimitSource;
  provider: string | null;
  baseline: number | null;
}) {
  return {
    unit: definition.unit,
    limitSource: definition.limitSource,
    provider: definition.provider,
    alertLow: definition.limits.alertLow,
    alertHigh: definition.limits.alertHigh,
    dangerLow: definition.limits.dangerLow,
    dangerHigh: definition.limits.dangerHigh,
    lowInclusive: definition.limits.lowInclusive,
    highInclusive: definition.limits.highInclusive,
    baselineValue: definition.baseline,
  };
}

function valuesOf(definition: RowDefinition) {
  return pointValues(definition);
}
